import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Input, Textarea, FormControl, FormLabel, Alert, Sheet, Container, Grid, CircularProgress, LinearProgress } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { useEmployer } from '../../../context/EmployerContext';
import { updateEmployer } from '../../../apiconfig/api';
import { uploadVideoWithPresignedUrl } from '../../../components/steps/functions/uploadVideoWithPresignedUrl';
import { getFileExtensionFromMime } from '../../../utils/getFileExtension';
import { upsertMedia } from '../../../apiconfig/api';
import { v4 as uuidv4 } from 'uuid';
import useDeviceInfo from '../../../hooks/useDeviceInfo';
import GeekPlayer from '../../../components/GeekPlayer';
import { stopAllVideos } from '../../../hooks/useVideoCleanup';

const EmployerSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { employer, loading, error: contextError, refreshEmployer } = useEmployer();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [aboutUs, setAboutUs] = useState('');
  const [introVideo, setIntroVideo] = useState<File | null>(null);
  
  // Video upload state
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedVideoKey, setUploadedVideoKey] = useState<string>('');
  const [showExistingVideo, setShowExistingVideo] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);
  
  const deviceInfo = useDeviceInfo();

  // Initialize form with employer data
  useEffect(() => {
    if (employer) {
      setDisplayName(employer.companyName || '');
      setUrlSlug(employer.slug || '');
      setCompanyAddress(employer.companyAddress || '');
      setZipCode((employer as any).zipCode || '');
      setAboutUs(employer.about || '');
      
      // Show existing intro video if available
      if (employer.introVideoUuid) {
        setShowExistingVideo(true);
      }
    }
  }, [employer]);

  const handleSave = async () => {
    if (!employer) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        companyName: displayName,
        slug: urlSlug,
        companyAddress: companyAddress,
        zipCode: zipCode,
        about: aboutUs,
        introVideoUuid: uploadedVideoKey || (employer as any).introVideoUuid,
      };

      await updateEmployer(employer.uuid, payload);
      
      // Refresh employer data to get updated values
      await refreshEmployer();
      
      // Stop all videos before navigation
      setVideoPlaying(false);
      stopAllVideos();
      
      // Give a moment for cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Navigate based on browser history
      if (window.history.length > 1) {
        navigate(-1); // Go back to previous page
      } else {
        navigate('/hiring/positions'); // Default fallback
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await processVideoFile(file);
    }
  };

  const processVideoFile = async (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file.');
      return;
    }

    setIntroVideo(file);
    // Create local URL for preview
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setShowExistingVideo(false);
    
    // Upload video immediately
    await uploadVideo(file);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await processVideoFile(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };



  const uploadVideo = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      const extension = getFileExtensionFromMime(
        file.type || "video/mp4"
      );

      const uploadFile = new File([file], `intro-video.${extension}`, {
        type: file.type || "video/mp4",
      });

      // Create media metadata and get upload URL
      const mediaId = uuidv4();
      const metadataPayload = {
        id: mediaId,
        original_file_key: uploadFile.name,
        original_file_mime_type: uploadFile.type,
        metadata: {
          metadata_version: new Date().toISOString().split("T")[0],
          source: "upload",
          upload_device: {
            name:
              deviceInfo.device.vendor && deviceInfo.device.model
                ? `${deviceInfo.device.vendor} ${deviceInfo.device.model}`
                : null,
            manufacturer: deviceInfo.device.vendor ?? null,
            model: deviceInfo.device.model ?? null,
            version: deviceInfo.browser.version ?? null,
            os: deviceInfo.os.name ?? null,
            os_version: deviceInfo.os.version ?? null,
          },
        },
      };

      const mediaResult = await upsertMedia(mediaId, metadataPayload);
      const uploadUrl = (mediaResult as any).data?.uploadUrl || (mediaResult as any).uploadUrl;

      if (!uploadUrl) {
        throw new Error("Upload URL is missing from media metadata response.");
      }

      // Upload the video using the URL from media metadata
      await uploadVideoWithPresignedUrl({
        file: uploadFile,
        uploadUrl: uploadUrl,
        onProgress: (progress) => {
          console.log('Upload progress:', progress);
          setUploadProgress(progress);
        },
      });

      // Store the media ID as the intro video UUID
      setUploadedVideoKey(mediaId);

      setUploadProgress(100);
      setIsUploading(false);
    } catch (error) {
      console.error("Video upload failed:", error);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const validateUrlSlug = (slug: string) => {
    if (slug.length >= 100) return 'URL slug must be fewer than 100 characters';
    if (!/^[a-z0-9-]+$/.test(slug)) return 'URL slug can only contain lowercase letters, numbers, and hyphens';
    return null;
  };

  const urlSlugError = validateUrlSlug(urlSlug);

  const inputStyles = {
    height: '56px',
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Input-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  const textareaStyles = {
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Textarea-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        bgcolor: "#f4f4f4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (!employer) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          padding: '20px',
        }}
      >
        <Typography level="h2" sx={{ fontWeight: 700 }}>
          Employer not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: 4 }}>
      <Container maxWidth="lg">
        <Typography level="h2" sx={{ mb: 3, fontWeight: 700 }}>
          Employer Settings
        </Typography>

        <Sheet
          sx={{
            width: '100%',
            padding: '40px',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        >

        {(error || contextError) && (
          <Alert 
            color="danger" 
            sx={{ 
              marginBottom: '24px',
              borderRadius: '8px'
            }}
          >
            {error || contextError}
          </Alert>
        )}

        {success && (
          <Alert 
            color="success" 
            sx={{ 
              marginBottom: '24px',
              borderRadius: '8px'
            }}
          >
            {success}
          </Alert>
        )}

                <Grid container spacing={3}>
          {/* Left Column - Form Fields */}
          <Grid xs={12} md={8}>
            <Stack spacing={3}>
              {/* Display Name */}
              <FormControl>
                <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  Display Name
                </FormLabel>
                <Input
                  value={displayName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
                  placeholder="e.g. Checker Hall"
                  sx={inputStyles}
                />
              </FormControl>

              {/* URL Slug */}
              <FormControl error={!!urlSlugError}>
                <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  URL Slug
                </FormLabel>
                <Input
                  value={urlSlug}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrlSlug(e.target.value.toLowerCase())}
                  placeholder="e.g. checker-hall"
                  sx={inputStyles}
                />
                {urlSlugError && (
                  <Typography level="body-sm" color="danger" marginTop="5px">
                    {urlSlugError}
                  </Typography>
                )}
              </FormControl>

              {/* Location */}
              <FormControl>
                <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  Location
                </FormLabel>
                <Textarea
                  value={companyAddress}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCompanyAddress(e.target.value)}
                  placeholder="e.g. 1234 Any Street, Sometown, CA"
                  minRows={2}
                  sx={textareaStyles}
                />
              </FormControl>

              {/* Zip Code */}
              <FormControl>
                <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  Zip Code
                </FormLabel>
                <Input
                  value={zipCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setZipCode(e.target.value)}
                  placeholder="e.g. 90210"
                  sx={inputStyles}
                />
              </FormControl>

              {/* About Us */}
              <FormControl>
                <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  About Us
                </FormLabel>
                <Textarea
                  value={aboutUs}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAboutUs(e.target.value)}
                  placeholder="Describe your company's history and brand..."
                  minRows={6}
                  sx={textareaStyles}
                />
                <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                  {aboutUs.length} characters (recommended: 300-500)
                </Typography>
              </FormControl>

              {/* Intro Video - Mobile Only */}
              <FormControl sx={{ display: { xs: 'block', md: 'none' } }}>
                <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                  Intro Video
                </FormLabel>
                <Box>
                  {videoUrl ? (
                    <Box sx={{ 
                      width: '100%',
                      aspectRatio: '3/4',
                      backgroundColor: '#000',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                      mb: 2
                    }}>
                      <GeekPlayer
                        localVideoUrl={videoUrl}
                        playing={videoPlaying}
                      />
                      <Button
                        size="sm"
                        variant="plain"
                        color="danger"
                        sx={{ 
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.9)',
                          }
                        }}
                        onClick={() => {
                          setIntroVideo(null);
                          setVideoUrl('');
                          URL.revokeObjectURL(videoUrl);
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        style={{ display: 'none' }}
                        id="intro-video-upload-mobile"
                      />
                      <label htmlFor="intro-video-upload-mobile">
                        <Button
                          component="span"
                          variant="outlined"
                          fullWidth
                          sx={{ 
                            height: '56px',
                            fontSize: '1.125rem',
                            borderColor: '#ECEFF3',
                            color: '#1A1B1D',
                            backgroundColor: '#ECEFF3',
                            '&:hover': {
                              backgroundColor: '#E0E3E7',
                              borderColor: '#E0E3E7',
                            }
                          }}
                        >
                          Upload Intro Video
                        </Button>
                      </label>
                    </>
                  )}
                  
                  {isUploading && (
                    <Box sx={{ mt: 2 }}>
                      <Typography level="body-sm" sx={{ color: '#2e2d7e', mb: 1, fontWeight: 500 }}>
                        Do not navigate to a new page while your video is uploading
                      </Typography>
                      <LinearProgress 
                        variant="solid"
                        determinate
                        value={uploadProgress} 
                        sx={{
                          backgroundColor: '#E0E3E7',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#2e2d7e',
                          }
                        }}
                      />
                      <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                        {uploadProgress}% uploaded
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                    Recommended: 1080p, 9:16 aspect ratio, 90 seconds or less, MP4/MOV/WEBM
                  </Typography>
                </Box>
              </FormControl>

              {/* Save Button - Mobile Only */}
              <Button
                variant="solid"
                fullWidth
                onClick={handleSave}
                disabled={saving || isUploading || !!urlSlugError}
                sx={{
                  height: '56px',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginTop: '24px',
                  backgroundColor: '#2e2d7e',
                  '&:hover': { backgroundColor: '#1e1d6e' },
                  display: { xs: 'block', md: 'none' }
                }}
              >
                {saving ? 'Saving...' : isUploading ? 'Uploading...' : 'Save Settings'}
              </Button>
            </Stack>
          </Grid>

          {/* Right Column - Video Upload */}
          <Grid xs={12} md={4}>
            <Box sx={{ 
              display: { xs: 'none', md: 'block' },
              height: '100%',
              minHeight: '400px'
            }}>
              <Typography sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                Intro Video
              </Typography>
              
              {videoUrl ? (
                <Box sx={{ 
                  width: '100%',
                  aspectRatio: '3/4',
                  backgroundColor: '#000',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <GeekPlayer
                    localVideoUrl={videoUrl}
                    playing={videoPlaying}
                  />
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    sx={{ 
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.9)',
                      }
                    }}
                    onClick={() => {
                      setIntroVideo(null);
                      setVideoUrl('');
                      URL.revokeObjectURL(videoUrl);
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : showExistingVideo ? (
                <Box sx={{ 
                  width: '100%',
                  aspectRatio: '3/4',
                  backgroundColor: '#000',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '2px dashed #7B7F85',
                  '&:hover': {
                    borderColor: '#2e2d7e'
                  }
                }}
                onClick={() => document.getElementById('intro-video-upload-desktop')?.click()}
              >
                <GeekPlayer
                  key={employer.introVideoUuid}
                  videoId={employer.introVideoUuid}
                  playing={videoPlaying}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    pointerEvents: 'none'
                  }}
                >
                  <Typography level="body-sm">Click to replace video</Typography>
                </Box>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  style={{ display: 'none' }}
                  id="intro-video-upload-desktop"
                />
              </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '3/4',
                    backgroundColor: '#ECEFF3',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed #7B7F85',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#E0E3E7',
                      borderColor: '#2e2d7e'
                    }
                  }}
                  onClick={() => document.getElementById('intro-video-upload-desktop')?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    style={{ display: 'none' }}
                    id="intro-video-upload-desktop"
                  />
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography level="body-sm" sx={{ color: '#7B7F85', mb: 1 }}>
                      Drag & drop video here
                    </Typography>
                    <Typography level="body-xs" sx={{ color: '#7B7F85' }}>
                      or click to browse
                    </Typography>
                  </Box>
                </Box>
              )}
              <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                Recommended: 1080p, 3:4 aspect ratio, 90 seconds or less, MP4/MOV/WEBM
              </Typography>
              
              {isUploading && (
                <Box sx={{ mt: 2 }}>
                  <Typography level="body-sm" sx={{ color: '#2e2d7e', mb: 1, fontWeight: 500 }}>
                    Do not navigate to a new page while your video is uploading
                  </Typography>
                  <LinearProgress 
                    variant="solid"
                    determinate
                    value={uploadProgress} 
                    sx={{
                      backgroundColor: '#E0E3E7',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#2e2d7e',
                      }
                    }}
                  />
                  <Typography level="body-sm" sx={{ mt: 1, color: '#7B7F85' }}>
                    {uploadProgress}% uploaded
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Save Button - Desktop Only */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'flex-start',
          mt: 3
        }}>
          <Button
            variant="solid"
            onClick={handleSave}
            disabled={saving || isUploading || !!urlSlugError}
            sx={{
              height: '56px',
              fontSize: '18px',
              fontWeight: 600,
              px: 4,
              backgroundColor: '#2e2d7e',
              '&:hover': { backgroundColor: '#1e1d6e' }
            }}
                      >
              {saving ? 'Saving...' : isUploading ? 'Uploading...' : 'Save Settings'}
            </Button>
          </Box>
        </Sheet>
      </Container>
    </Box>
  );
};

export default EmployerSettingsPage; 